import React, { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import type { Moment } from '@this/src/lib/moment';
import moment from '@this/src/lib/moment';
import { Fetcher } from '@this/src/util';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import { Alert } from '@this/shared/ui/feedbacks/alert';
import { Button } from '@this/shared/ui/inputs/button';
import { Input } from '@this/src/components/shared/ui/inputs/input';
import { Checkbox } from '@this/src/components/shared/ui/inputs/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@this/src/components/shared/ui/data_displays/table';
import { Link } from '@this/shared/ui/navigations/link';
import type { MessageBulkSendTodoResponseArgs } from '@this/src/domain/arrangement/message_bulk_send_todo';
import MessageBulkSendTodo, {
  convertMessageBulkSendTodoResponseToArgs
} from '@this/src/domain/arrangement/message_bulk_send_todo';
import TripStatus from '@this/src/domain/trip/trip_status';
import type { MessageTemplate } from '@this/components/arrangement/virtual_counter/types';
import { SearchArea, SearchBlock, SearchLabel } from '../search_area';
import DateRangePicker from '../date_range_picker';
import MessageBulkSendModal from './message_bulk_send_modal';

interface ListResponse {
  message_bulk_send_todos: MessageBulkSendTodoResponseArgs[];
  total_count: number;
}

interface MessageTemplatesResponse {
  message_templates: MessageTemplate[];
}

interface BulkSendResult {
  trip_id: number;
  status: 'success' | 'failed';
  error?: string;
}

interface BulkSendResponse {
  success: boolean;
  results: BulkSendResult[];
}

const MessageBulkSendList = observer(() => {
  const [todos, setTodos] = useState<MessageBulkSendTodo[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [startDateFrom, setStartDateFrom] = useState<Moment | undefined>(() => moment().startOf('day'));
  const [startDateTo, setStartDateTo] = useState<Moment | undefined>(undefined);
  const [keyword, setKeyword] = useState<string>('');
  const [debouncedKeyword, setDebouncedKeyword] = useState<string>('');
  const [checkedTripIds, setCheckedTripIds] = useState<Set<number>>(new Set());
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [messageTemplates, setMessageTemplates] = useState<MessageTemplate[]>([]);
  const [hasLoadedMessageTemplates, setHasLoadedMessageTemplates] = useState<boolean>(false);
  const [isLoadingMessageTemplates, setIsLoadingMessageTemplates] = useState<boolean>(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedKeyword(keyword), 500);
    return () => clearTimeout(timer);
  }, [keyword]);

  useEffect(() => {
    if (!isModalOpen || hasLoadedMessageTemplates || isLoadingMessageTemplates) {
      return;
    }
    setIsLoadingMessageTemplates(true);
    Fetcher.get<MessageTemplatesResponse>('/arrangement/message_templates.json')
      .then(res => {
        setMessageTemplates(res.message_templates);
        setHasLoadedMessageTemplates(true);
      })
      .catch(() => setErrorMessage('テンプレート一覧の取得に失敗しました。'))
      .finally(() => setIsLoadingMessageTemplates(false));
  }, [hasLoadedMessageTemplates, isLoadingMessageTemplates, isModalOpen]);

  const fetchTodos = useCallback(() => {
    setIsLoading(true);
    const params = {
      start_date_from: startDateFrom?.format('YYYY-MM-DD'),
      start_date_to: startDateTo?.format('YYYY-MM-DD'),
      keyword: debouncedKeyword.length > 0 ? debouncedKeyword : undefined
    };
    Object.keys(params).forEach(key => {
      if (params[key as keyof typeof params] === undefined) {
        delete params[key as keyof typeof params];
      }
    });

    Fetcher.get<ListResponse>('/arrangement/message_bulk_send_todos.json', params)
      .then(res => {
        setTodos(
          res.message_bulk_send_todos.map(
            args => new MessageBulkSendTodo(convertMessageBulkSendTodoResponseToArgs(args))
          )
        );
        setTotalCount(res.total_count);
        setCheckedTripIds(new Set());
      })
      .catch(() => {
        setErrorMessage('一覧の取得に失敗しました。');
      })
      .finally(() => setIsLoading(false));
  }, [startDateFrom, startDateTo, debouncedKeyword]);

  useEffect(() => {
    fetchTodos();
  }, [fetchTodos]);

  const handleToggleAll = useCallback(() => {
    if (checkedTripIds.size === todos.length) {
      setCheckedTripIds(new Set());
    } else {
      setCheckedTripIds(new Set(todos.map(t => t.tripId)));
    }
  }, [checkedTripIds, todos]);

  const handleToggleOne = useCallback(
    (tripId: number) => {
      const next = new Set(checkedTripIds);
      if (next.has(tripId)) {
        next.delete(tripId);
      } else {
        next.add(tripId);
      }
      setCheckedTripIds(next);
    },
    [checkedTripIds]
  );

  const handleMessageFinish = useCallback(
    (messageId: number) => {
      Fetcher.put(`/arrangement/messages/${messageId}/finish`, {}).then(() => fetchTodos());
    },
    [fetchTodos]
  );

  const handleBulkSubmit = useCallback(
    async (body: string) => {
      setErrorMessage(null);
      setSuccessMessage(null);
      const tripIds = Array.from(checkedTripIds);

      try {
        const res = await Fetcher.post<BulkSendResponse>('/arrangement/bulk_messages', {
          trip_ids: tripIds,
          message_body: body
        });

        const successCount = res.results.filter(r => r.status === 'success').length;
        const failedCount = res.results.filter(r => r.status === 'failed').length;

        if (failedCount > 0) {
          const failedTripIds = res.results
            .filter(r => r.status === 'failed')
            .map(r => r.trip_id)
            .join(', ');
          setErrorMessage(
            `一部の旅程で送信に失敗しました（成功: ${successCount}件 / 失敗: ${failedCount}件） 失敗Trip ID: ${failedTripIds}`
          );
        } else {
          setSuccessMessage(`${successCount}件の旅程に送信しました。`);
        }

        setIsModalOpen(false);
        fetchTodos();
      } catch (e) {
        setErrorMessage('送信に失敗しました。時間をおいて再度お試しください。');
        setIsModalOpen(false);
      }
    },
    [checkedTripIds, fetchTodos]
  );

  const allChecked = todos.length > 0 && checkedTripIds.size === todos.length;
  const someChecked = checkedTripIds.size > 0 && checkedTripIds.size < todos.length;

  return (
    <>
      <SearchArea>
        <SearchBlock>
          <SearchLabel>出発日</SearchLabel>
          <DateRangePicker
            from={startDateFrom}
            to={startDateTo}
            onFromChange={d => setStartDateFrom(d)}
            onToChange={d => setStartDateTo(d)}
          />
        </SearchBlock>
        <SearchBlock>
          <SearchLabel>キーワード（チャット本文部分一致）</SearchLabel>
          <Input
            value={keyword}
            onChange={e => setKeyword(e.target.value)}
            placeholder="本文を含むキーワード"
            style={{ width: '240px' }}
          />
        </SearchBlock>
      </SearchArea>
      {errorMessage && (
        <Alert variant="danger" onClose={() => setErrorMessage(null)} style={{ marginBottom: '12px' }}>
          {errorMessage}
        </Alert>
      )}
      {successMessage && (
        <Alert variant="success" onClose={() => setSuccessMessage(null)} style={{ marginBottom: '12px' }}>
          {successMessage}
        </Alert>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '10px 0' }}>
        <Button
          data-testid="message-bulk-send-open-modal"
          onClick={() => setIsModalOpen(true)}
          disabled={checkedTripIds.size === 0}
        >
          メッセージ入力
        </Button>
        <span>{`チェック中: ${checkedTripIds.size}件 / 検索結果: ${totalCount}件`}</span>
      </div>
      {isLoading ? (
        <Loading />
      ) : (
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>
                <Checkbox
                  checked={allChecked}
                  indeterminate={someChecked}
                  onChange={handleToggleAll}
                  disabled={todos.length === 0}
                />
              </TableCell>
              <TableCell>Tripステータス</TableCell>
              <TableCell>ステータス</TableCell>
              <TableCell>ステータス最終更新者</TableCell>
              <TableCell>カテゴリ</TableCell>
              <TableCell>シーケンス</TableCell>
              <TableCell>変更</TableCell>
              <TableCell>法人名</TableCell>
              <TableCell>SP</TableCell>
              <TableCell>QR</TableCell>
              <TableCell>土日祝対応</TableCell>
              <TableCell>引き継ぎ</TableCell>
              <TableCell>発券方法</TableCell>
              <TableCell>券種</TableCell>
              <TableCell nowrap>出発日時</TableCell>
              <TableCell nowrap>依頼日</TableCell>
              <TableCell nowrap>キャンセル依頼日時</TableCell>
              <TableCell nowrap>購入期限日</TableCell>
              <TableCell>チャットメッセージ</TableCell>
              <TableCell nowrap>Trip ID</TableCell>
              <TableCell nowrap>Trace ID</TableCell>
              <TableCell>最新のログメッセージ</TableCell>
              <TableCell>アクション</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {todos.map(todo => (
              <TableRow key={todo.tripId}>
                <TableCell>
                  <Checkbox
                    checked={checkedTripIds.has(todo.tripId)}
                    onChange={() => handleToggleOne(todo.tripId)}
                  />
                </TableCell>
                <TableCell>{TripStatus[todo.tripStatus] || '-'}</TableCell>
                <TableCell>-</TableCell>
                <TableCell>-</TableCell>
                <TableCell>メッセージ</TableCell>
                <TableCell>-</TableCell>
                <TableCell>-</TableCell>
                <TableCell>{todo.organizationName || '-'}</TableCell>
                <TableCell>-</TableCell>
                <TableCell>-</TableCell>
                <TableCell>-</TableCell>
                <TableCell>-</TableCell>
                <TableCell>-</TableCell>
                <TableCell>-</TableCell>
                <TableCell nowrap>{todo.startDate ? todo.startDate.format('YYYY-MM-DD') : '-'}</TableCell>
                <TableCell nowrap>{todo.receivedAt ? todo.receivedAt.format('YYYY-MM-DD HH:mm') : '-'}</TableCell>
                <TableCell>-</TableCell>
                <TableCell>-</TableCell>
                <TableCell style={{ maxWidth: '300px', whiteSpace: 'pre-wrap' }}>
                  {todo.lastUserMessage || '-'}
                </TableCell>
                <TableCell nowrap>
                  <Link href={`/arrangement/virtual_counter?trip_id=${todo.tripId}`} target="_blank">
                    {todo.tripId}
                  </Link>
                </TableCell>
                <TableCell nowrap>-</TableCell>
                <TableCell>-</TableCell>
                <TableCell>
                  {todo.lastUserMessageId && (
                    <Button
                      size="small"
                      onClick={() => todo.lastUserMessageId && handleMessageFinish(todo.lastUserMessageId)}
                    >
                      対応不要
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <MessageBulkSendModal
        isOpen={isModalOpen}
        checkedCount={checkedTripIds.size}
        messageTemplates={messageTemplates}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleBulkSubmit}
      />
    </>
  );
});

export default MessageBulkSendList;
